import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import { salesCategoryNamesInHierarchy } from "api-types/builders/salesCategory";
import type {
  ClientLocationPlan,
  ClientLocationPlanWithOverrides,
} from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import type { ClientLotCommunityPlanHydrated } from "api-types/lotCommunityPlans/lotCommunityPlansResponses";
import type { ClientLot } from "api-types/lots/lotsResponses";
import type { ClientMasterPlan, ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import type { Doc } from "building-types/doc";
import type { DocOptionEffect } from "building-types/options/optionEffect";
import type { DocOption, DocOptionSet } from "building-types/options/optionSet";
import type { OptionSetOverride } from "building-types/options/optionTemplateTypes";
import type { Table } from "common/data/table";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import stringApplySearch from "common/utils/stringApplySearch";
import mergeOptionTemplatesIntoOptionSets from "doc/options/mergeOptionTemplatesIntoOptionSets";
import { getRulesByOptions } from "doc/options/optionRule";
import { optionSetEntityRelationsFromDoc } from "doc/options/relations/optionSetEntityRelations";
import { isElevationOptionSet } from "doc/options/systemOptionSet";
import { filterToUnitLevelOptionSets } from "lib/options/reportingUnitDocFilters";
import { getPrimarySalesCategoryCounts } from "lib/state/salesCategory";
import { updateLocationPlanOverrides } from "lib/storage/locationPlanStorage";
import { updateLotCommunityPlan } from "lib/storage/lotStorage";
import { updateMasterPlanOverrides } from "lib/storage/masterPlanStorage";
import {
  PlanLibraryStateType,
  type PlanLibraryOptionRuleData,
  type PlanLibraryOptionSet,
  type PlanLibraryPlans,
  type PlanLibraryState,
} from "./planLibraryTypes";
import {
  planLibraryCreateOptionSetOverride,
  planLibraryCreateOptionSets,
  planLibraryCreateSalesCategories,
  planLibraryDiscardOptionPropOverride,
  planLibraryDiscardOptionSetPropOverride,
  pushParentMovedItemsToEnd,
  withElevationOptionSet,
} from "./planLibraryUtils";

export const planLibraryDefaultState: PlanLibraryState = Object.freeze({
  state: PlanLibraryStateType.UNINITIALIZED,
});

const planLibraryInitializedDefaults = Object.freeze({
  originalOptionRules: {},
  originalOptionEffects: {},
  optionEffectOverrides: {},
  optionRuleRefsByOptionSets: {},

  needsSaving: false,
  discardAllOverrides: false,
  massEditing: false,
  hideEmptySalesCategories: true,
  searchFor: "",
});

function getParentSalesCategoryMap(plans: PlanLibraryPlans): Record<string, string> {
  if (plans.planType === "master_plan") {
    return {};
  }
  const map: Record<string, string> = {};
  for (const [id, override] of Object.entries(plans.masterPlan.overrides ?? {})) {
    if (override.salesCategory) {
      map[id] = override.salesCategory;
    }
  }
  if (plans.planType === "lot") {
    for (const [id, override] of Object.entries(plans.locationPlan.overrides ?? {})) {
      if (override.salesCategory) {
        map[id] = override.salesCategory;
      }
    }
  }
  return map;
}

function replaceOldOptionSet(
  state: PlanLibraryState,
  oldOptionSet: PlanLibraryOptionSet,
  newOptionSet: PlanLibraryOptionSet,
) {
  if (state.state === PlanLibraryStateType.UNINITIALIZED) {
    return;
  }

  if (isElevationOptionSet(newOptionSet.id)) {
    state.elevationOptionSet = newOptionSet;
  } else {
    const optionSetsPerCategory = oldOptionSet.isBooleanOptionSet
      ? state.booleanOptionSetsPerSalesCategory
      : state.nonBoolOptionSetsPerSalesCategory;
    const oldCategory = oldOptionSet.salesCategory;
    const newCategory = newOptionSet.salesCategory;

    if (oldCategory === newCategory) {
      // option set has not changed sales category
      optionSetsPerCategory[oldCategory] = optionSetsPerCategory[oldCategory].map((x) => {
        return x.id === newOptionSet.id ? newOptionSet : x;
      });
    } else {
      // option set has changed sales category
      const n = optionSetsPerCategory[newCategory].reduce(
        (n, { sortIndex }) => (n <= sortIndex ? sortIndex + 1 : n),
        newOptionSet.sortIndex, // initial value of n
      );

      if (n > newOptionSet.sortIndex) {
        // ensures option is positioned at end of new sales category via a sortIndex override
        const oldOverride = state.overrides[newOptionSet.id];
        const newOverride = { ...oldOverride, sortIndex: n };
        state.overrides[newOptionSet.id] = newOverride;
        newOptionSet = { ...newOptionSet, sortIndex: n };
      }

      const prev = optionSetsPerCategory[oldCategory].filter(({ id }) => id !== oldOptionSet.id);
      const next = optionSetsPerCategory[newCategory].concat(newOptionSet);
      optionSetsPerCategory[oldCategory] = prev;
      optionSetsPerCategory[newCategory] = next;

      if (!newOptionSet.isFilteredBySearch) {
        state.salesCategorySizes[oldCategory]--;
        state.salesCategorySizes[newCategory]++;
        state.primarySalesCategorySizes = getPrimarySalesCategoryCounts(
          state.salesCategoryNames,
          state.salesCategorySizes,
          state.builder.salesConfig?.salesCategoryCustomization?.hierarchy,
        );
      }
    }
  }

  state.needsSaving = true;
}

const planLibrarySlice = createSlice({
  name: "planLibrary",
  initialState: planLibraryDefaultState as PlanLibraryState,
  reducers: {
    initForMasterPlan(
      _state,
      action: PayloadAction<{
        publishedDoc: Doc;
        masterPlan: ClientMasterPlanHydrated;
        builder: ClientBuilder;
        isReadOnly: boolean;
      }>,
    ) {
      const { publishedDoc, masterPlan, builder, isReadOnly } = action.payload;

      const salesCategoryHierarchy = builder.salesConfig?.salesCategoryCustomization?.hierarchy;
      const everySalesCategoryName = salesCategoryNamesInHierarchy(salesCategoryHierarchy, true);
      const inheritedOptionSets = publishedDoc.optionSets;

      const optionSets = masterPlan.doc.optionSets;

      const {
        salesCategorySizes,
        salesCategoryNames,
        primarySalesCategorySizes,
        primarySalesCategoryNames,
        primarySalesCategoryKeyToNameMap,
        secondaryToPrimarySalesCategoryMap,
        booleanOptionSetsPerSalesCategory,
        nonBoolOptionSetsPerSalesCategory,
        elevationOptionSet,
      } = planLibraryCreateSalesCategories(inheritedOptionSets, optionSets, everySalesCategoryName, builder);

      const overrides = masterPlan.overrides ?? {};
      const planLibraryInheritedOptionSets = planLibraryCreateOptionSets(inheritedOptionSets, builder);

      const initializedState: PlanLibraryState = {
        state: PlanLibraryStateType.INITIALIZED,
        ...planLibraryInitializedDefaults,
        builder,

        plans: {
          planType: "master_plan",
          masterPlan,
        },

        optionSetEntityRelations: optionSetEntityRelationsFromDoc(publishedDoc),
        inheritedOptionSets: planLibraryInheritedOptionSets,
        unchangedOptionSets: inheritedOptionSets,
        overrides,

        originalOptionRules: publishedDoc.optionRules,
        originalOptionEffects: publishedDoc.optionEffects,
        optionRuleRefsByOptionSets: getRulesByOptions(
          publishedDoc.optionRules,
          publishedDoc.optionEffects,
          publishedDoc.optionSets,
        ),
        optionEffectOverrides: masterPlan.optionEffectOverrides ?? {},

        salesCategorySizes,
        salesCategoryNames,
        primarySalesCategorySizes,
        primarySalesCategoryNames,
        primarySalesCategoryKeyToNameMap,
        secondaryToPrimarySalesCategoryMap,
        booleanOptionSetsPerSalesCategory,
        nonBoolOptionSetsPerSalesCategory,
        elevationOptionSet,

        planName: masterPlan.name,
        planId: masterPlan.id,

        isReadOnly,
      };

      return initializedState;
    },

    initForLocationPlan(
      _state,
      action: PayloadAction<{
        locationPlan: ClientLocationPlanWithOverrides;
        masterPlan: ClientMasterPlanHydrated;
        location: ClientLocationWithLevel;
        builder: ClientBuilder;
        optionSets: Table<DocOptionSet>;
        optionEffects: Table<DocOptionEffect>;
        isReadOnly: boolean;
      }>,
    ) {
      const { locationPlan, masterPlan, location, builder, isReadOnly, optionSets, optionEffects } = action.payload;

      const salesCategoryHierarchy = builder.salesConfig?.salesCategoryCustomization?.hierarchy;
      const everySalesCategoryName = salesCategoryNamesInHierarchy(salesCategoryHierarchy, true);
      const inheritedOptionSets = masterPlan.doc.optionSets;

      const {
        salesCategorySizes,
        salesCategoryNames,
        primarySalesCategorySizes,
        primarySalesCategoryNames,
        primarySalesCategoryKeyToNameMap,
        secondaryToPrimarySalesCategoryMap,
        booleanOptionSetsPerSalesCategory,
        nonBoolOptionSetsPerSalesCategory,
        elevationOptionSet,
      } = planLibraryCreateSalesCategories(inheritedOptionSets, optionSets, everySalesCategoryName, builder);

      const overrides = locationPlan.overrides ?? {};
      const plans = {
        planType: "community_plan" as const,
        locationPlan,
        masterPlan,
        location,
      };
      const parentSalesCategoryMap = getParentSalesCategoryMap(plans);
      const planLibraryInheritedOptionSets = planLibraryCreateOptionSets(inheritedOptionSets, builder);
      pushParentMovedItemsToEnd(booleanOptionSetsPerSalesCategory, overrides, parentSalesCategoryMap);
      pushParentMovedItemsToEnd(nonBoolOptionSetsPerSalesCategory, overrides, parentSalesCategoryMap);

      const initializedState: PlanLibraryState = {
        state: PlanLibraryStateType.INITIALIZED,
        ...planLibraryInitializedDefaults,

        plans,
        builder,

        optionSetEntityRelations: optionSetEntityRelationsFromDoc(masterPlan.doc),
        inheritedOptionSets: planLibraryInheritedOptionSets,
        unchangedOptionSets: inheritedOptionSets,
        overrides,

        originalOptionRules: masterPlan.doc.optionRules,
        originalOptionEffects: optionEffects,
        optionRuleRefsByOptionSets: getRulesByOptions(masterPlan.doc.optionRules, optionEffects, optionSets),
        optionEffectOverrides: locationPlan.optionEffectOverrides ?? {},

        salesCategorySizes,
        salesCategoryNames,
        primarySalesCategorySizes,
        primarySalesCategoryNames,
        primarySalesCategoryKeyToNameMap,
        secondaryToPrimarySalesCategoryMap,
        booleanOptionSetsPerSalesCategory,
        nonBoolOptionSetsPerSalesCategory,
        elevationOptionSet,

        planName: locationPlan.name,
        planId: locationPlan.id,

        isReadOnly,
      };

      return initializedState;
    },

    initForLotCommunityPlan(
      _,
      action: PayloadAction<{
        lotCommunityPlan: ClientLotCommunityPlanHydrated;
        locationPlan: ClientLocationPlanWithOverrides;
        masterPlan: ClientMasterPlanHydrated;
        location: ClientLocationWithLevel;
        optionSets: Table<DocOptionSet>;
        optionEffects: Table<DocOptionEffect>;
        builder: ClientBuilder;
        lot: ClientLot;
        isReadOnly: boolean;
      }>,
    ) {
      const { lotCommunityPlan, locationPlan, masterPlan, location, builder, lot, isReadOnly } = action.payload;

      const salesCategoryHierarchy = builder.salesConfig?.salesCategoryCustomization?.hierarchy;
      const everySalesCategoryName = salesCategoryNamesInHierarchy(salesCategoryHierarchy, true);
      let inheritedOptionSets = action.payload.optionSets;

      // Multi-unit lots: unit-level options only, plus the building's elevations to display.
      // The elevation entry is display-only for unit lots — `elevationsReadOnly` in
      // PlanLibraryElevationsTab is what keeps it from being overridden here.
      if (lot.reportingUnitId) {
        inheritedOptionSets = withElevationOptionSet(
          filterToUnitLevelOptionSets(inheritedOptionSets, masterPlan.doc, lot.reportingUnitId),
          inheritedOptionSets,
        );
      }

      const optionSets = mergeOptionTemplatesIntoOptionSets(inheritedOptionSets, [lotCommunityPlan.optionSetOverrides]);

      const {
        salesCategorySizes,
        salesCategoryNames,
        primarySalesCategorySizes,
        primarySalesCategoryNames,
        primarySalesCategoryKeyToNameMap,
        secondaryToPrimarySalesCategoryMap,
        booleanOptionSetsPerSalesCategory,
        nonBoolOptionSetsPerSalesCategory,
        elevationOptionSet,
      } = planLibraryCreateSalesCategories(inheritedOptionSets, optionSets, everySalesCategoryName, builder);

      const overrides = lotCommunityPlan.optionSetOverrides ?? {};
      const plans = {
        planType: "lot" as const,
        lotCommunityPlan,
        locationPlan,
        masterPlan,
        location,
        lot,
      };
      const parentSalesCategoryMap = getParentSalesCategoryMap(plans);
      const planLibraryInheritedOptionSets = planLibraryCreateOptionSets(inheritedOptionSets, builder);
      pushParentMovedItemsToEnd(booleanOptionSetsPerSalesCategory, overrides, parentSalesCategoryMap);
      pushParentMovedItemsToEnd(nonBoolOptionSetsPerSalesCategory, overrides, parentSalesCategoryMap);

      const initializedState: PlanLibraryState = {
        state: PlanLibraryStateType.INITIALIZED,
        ...planLibraryInitializedDefaults,

        plans,
        builder,

        optionSetEntityRelations: optionSetEntityRelationsFromDoc(masterPlan.doc),
        inheritedOptionSets: planLibraryInheritedOptionSets,
        unchangedOptionSets: inheritedOptionSets,
        overrides,

        salesCategorySizes,
        salesCategoryNames,
        primarySalesCategorySizes,
        primarySalesCategoryNames,
        primarySalesCategoryKeyToNameMap,
        secondaryToPrimarySalesCategoryMap,
        booleanOptionSetsPerSalesCategory,
        nonBoolOptionSetsPerSalesCategory,
        elevationOptionSet,

        planName: lotCommunityPlan.name || `${masterPlan.name} on Lot ${lot.lotNumber}`,
        planId: lotCommunityPlan.id,

        isReadOnly,
      };

      return initializedState;
    },

    modifyLotCommunityPlan(state, action: PayloadAction<ClientLotCommunityPlanHydrated>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      if (state.plans.planType !== "lot") {
        return;
      }
      state.planName = action.payload.name || `${state.plans.masterPlan.name} on Lot ${state.plans.lot.lotNumber}`;
      state.plans.lotCommunityPlan.name = action.payload.name;
      state.plans.lotCommunityPlan.basePrice = action.payload.basePrice;
      state.plans.lotCommunityPlan.externalId = action.payload.externalId;
    },

    modifyCommunityPlan(state, action: PayloadAction<ClientLocationPlan>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      if (state.plans.planType !== "community_plan") {
        return;
      }
      state.planName = action.payload.name;
      state.plans.locationPlan.name = action.payload.name;
      state.plans.locationPlan.basePrice = action.payload.basePrice;
      state.plans.locationPlan.disableNewOptions = action.payload.disableNewOptions;
      state.plans.locationPlan.planDownloadEnabled = action.payload.planDownloadEnabled;
    },

    modifyMasterPlan(state, action: PayloadAction<ClientMasterPlan>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      if (state.plans.planType !== "master_plan") {
        return;
      }
      state.planName = action.payload.name;
      state.plans.masterPlan.name = action.payload.name;
      state.plans.masterPlan.basePrice = action.payload.basePrice;
      state.plans.masterPlan.planDownloadEnabled = action.payload.planDownloadEnabled;
    },

    reorderOptionSetsInSalesCategory(
      state: PlanLibraryState,
      action: PayloadAction<{
        salesCategoryName: string;
        sortTuples: { oldOptionSet: PlanLibraryOptionSet; newSortIndex: number }[];
      }>,
    ) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }

      const { salesCategoryName, sortTuples } = action.payload;

      if (sortTuples.length > 0) {
        const optionSetsPerCategory = sortTuples[0].oldOptionSet.isBooleanOptionSet
          ? state.booleanOptionSetsPerSalesCategory
          : state.nonBoolOptionSetsPerSalesCategory;

        const parentSalesCategoryMap = getParentSalesCategoryMap(state.plans);
        optionSetsPerCategory[salesCategoryName] = sortTuples.map(({ oldOptionSet, newSortIndex }) => {
          const newOptionSet = { ...oldOptionSet, sortIndex: newSortIndex };
          const oldOverride = state.overrides[oldOptionSet.id];
          const newOverride = planLibraryCreateOptionSetOverride(oldOptionSet, newOptionSet, oldOverride);

          // If a parent moved this item into the category and the current level
          // hasn't claimed it yet with its own override, write a salesCategory override to prevent
          // normalization from re-slotting it to the end on subsequent loads.
          const needsClaim =
            parentSalesCategoryMap[oldOptionSet.id] !== undefined && !state.overrides[oldOptionSet.id]?.salesCategory;

          if (newOverride) {
            state.overrides[newOptionSet.id] = needsClaim
              ? { ...newOverride, salesCategory: newOptionSet.salesCategory }
              : newOverride;
            state.needsSaving = true;
          } else if (needsClaim) {
            state.overrides[newOptionSet.id] = { salesCategory: newOptionSet.salesCategory };
            state.needsSaving = true;
          } else if (oldOverride) {
            // Discard the overrides if manually reverted to inherited option set
            for (const key of Object.keys(oldOverride)) {
              planLibraryDiscardOptionSetPropOverride(state.overrides, oldOptionSet.id, key);
            }
            state.needsSaving = true;
          }

          return newOptionSet;
        });
      }
    },

    overrideOptionSet(
      state: PlanLibraryState,
      action: PayloadAction<{ oldOptionSet: PlanLibraryOptionSet; newOptionSet: PlanLibraryOptionSet }>,
    ) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }

      const { oldOptionSet } = action.payload;
      let { newOptionSet } = action.payload;

      // Enforce invariant: the default option must always be enabled
      const defaultOption = newOptionSet.options.find((o) => o.id === newOptionSet.defaultValue);
      if (defaultOption?.disabled) {
        newOptionSet = {
          ...newOptionSet,
          options: newOptionSet.options.map((o) =>
            o.id === newOptionSet.defaultValue ? { ...o, disabled: false } : o,
          ),
        };
      }

      if (oldOptionSet.id === newOptionSet.id) {
        const oldOverride = state.overrides[newOptionSet.id];
        const newOverride = planLibraryCreateOptionSetOverride(oldOptionSet, newOptionSet, oldOverride);
        if (newOverride) {
          state.overrides[newOptionSet.id] = newOverride;
          replaceOldOptionSet(state, oldOptionSet, newOptionSet);

          // When a salesCategory change moves an item to a new category, claim any unclaimed
          // parent-moved items in the target category so they keep their position on next load
          // instead of being re-slotted to the end by pushParentMovedItemsToEnd.
          if (oldOptionSet.salesCategory !== newOptionSet.salesCategory) {
            const parentSalesCategoryMap = getParentSalesCategoryMap(state.plans);
            const targetCategory = newOptionSet.salesCategory;
            for (const categoryMap of [
              state.booleanOptionSetsPerSalesCategory,
              state.nonBoolOptionSetsPerSalesCategory,
            ]) {
              for (const os of categoryMap[targetCategory] ?? []) {
                if (parentSalesCategoryMap[os.id] !== undefined && !state.overrides[os.id]?.salesCategory) {
                  state.overrides[os.id] = { ...state.overrides[os.id], salesCategory: os.salesCategory };
                }
              }
            }
          }
        }
      }
    },

    enableDiscardAllOverridesModal(state, action: PayloadAction<boolean>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      state.discardAllOverrides = action.payload;
    },

    enableOverrideModal(
      state: PlanLibraryState,
      action: PayloadAction<{ optionSet: PlanLibraryOptionSet; option?: DocOption } | null>,
    ) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      if (action.payload) {
        const { optionSet, option } = action.payload;
        state.modalOptionSet = optionSet;
        state.modalOption = option;
      } else {
        state.modalOptionSet = undefined;
        state.modalOption = undefined;
      }
    },

    toggleOptionRuleModal(state, action: PayloadAction<{ editOptionRules: PlanLibraryOptionRuleData | null }>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      state.editOptionRules = action.payload?.editOptionRules ?? undefined;
    },

    overrideOptionEffectEnable(state, action: PayloadAction<{ id: string; optionId: string }>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      // if the optionEffect is the same as the original, then remove the override
      const originalOptionId = state.originalOptionEffects[action.payload.id]?.optionId;

      if (action.payload.optionId === originalOptionId) {
        delete state.optionEffectOverrides[action.payload.id];
      } else {
        state.optionEffectOverrides = {
          ...state.optionEffectOverrides,
          [action.payload.id]: { optionId: action.payload.optionId },
        };
      }
      state.needsSaving = true;
    },

    resetOptionEffectOverrides(state, action: PayloadAction<{ optionRuleId: string }>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      const { optionRuleId } = action.payload;
      for (const effectId of Object.keys(state.originalOptionEffects)) {
        if (state.originalOptionEffects[effectId].parentId === optionRuleId) {
          delete state.optionEffectOverrides[effectId];
        }
      }
      state.needsSaving = true;
    },

    resetOptionEffectOverride(state, action: PayloadAction<{ optionEffectId: string }>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      delete state.optionEffectOverrides[action.payload.optionEffectId];
      state.needsSaving = true;
    },

    setOverridesToDiscard(
      state,
      action: PayloadAction<{
        overridesToDiscard: OptionSetOverride;
        onHandleDiscarding: () => void;
      } | null>,
    ) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      if (action.payload) {
        const { overridesToDiscard, onHandleDiscarding } = action.payload;
        state.overridesToDiscard = overridesToDiscard;
        state.onHandleDiscarding = onHandleDiscarding;
      } else {
        state.overridesToDiscard = undefined;
        state.onHandleDiscarding = undefined;
      }
    },

    discardOptionOverride(
      state,
      action: PayloadAction<{
        optionSet: PlanLibraryOptionSet;
        optionId: string;
      }>,
    ) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      const { optionSet, optionId } = action.payload;

      const override = state.overrides[optionSet.id]?.options?.[optionId];
      if (override) {
        Object.keys(override).forEach((k) => {
          planLibraryDiscardOptionPropOverride(state.overrides, optionSet.id, optionId, k);
        });

        const inheritedOptionSets = state.inheritedOptionSets;
        const inherited = inheritedOptionSets[optionSet.id].options.find((x) => x.id === optionId); // replacement option
        const toReplace = optionSet.options.find((x) => x.id === optionId); // option to replace
        const sortIndex = toReplace?.sortIndex ?? 0; // keep to avoid random re(sort) chaos

        assertIsNotUndefined(inherited);

        const options = optionSet.options.map((x) => {
          return x.id === optionId ? { ...inherited, sortIndex } : x;
        });

        replaceOldOptionSet(state, optionSet, { ...optionSet, options });
      }
    },

    discardOptionSetOverride(state, action: PayloadAction<{ optionSet: PlanLibraryOptionSet }>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      const { optionSet } = action.payload;

      const inheritedOptionSet = state.inheritedOptionSets[optionSet.id];
      const optionSetOverrides = state.overrides[optionSet.id];
      if (optionSetOverrides) {
        for (const key of Object.keys(optionSetOverrides)) {
          planLibraryDiscardOptionSetPropOverride(state.overrides, optionSet.id, key);
        }

        const options = optionSet.options.map(({ id, sortIndex }) => {
          const inheritedOption = inheritedOptionSet.options.find((x) => x.id === id);
          const optionOverrides = optionSetOverrides.options?.[id];

          assertIsNotUndefined(inheritedOption);

          for (const key of Object.keys(optionOverrides ?? {})) {
            planLibraryDiscardOptionPropOverride(state.overrides, optionSet.id, id, key);
          }
          return {
            ...inheritedOption,
            sortIndex,
          };
        });

        replaceOldOptionSet(state, optionSet, {
          ...inheritedOptionSet,
          sortIndex: optionSet.sortIndex, // keep old option set sortIndex
          options,
        });
      }
    },

    discardOptionPropOverride(
      state,
      action: PayloadAction<{
        oldOptionSet: PlanLibraryOptionSet;
        optionId: string;
        key: keyof DocOption;
      }>,
    ) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      const { oldOptionSet, optionId, key } = action.payload;

      if (planLibraryDiscardOptionPropOverride(state.overrides, oldOptionSet.id, optionId, key)) {
        const options = oldOptionSet.options.map((x) => {
          if (x.id === optionId) {
            const inherited = state.inheritedOptionSets[oldOptionSet.id].options.find((x) => x.id === optionId);
            let inheritedValue = inherited?.[key];
            if (inheritedValue === undefined) {
              if (key === "salesImages") {
                inheritedValue = [];
              } else {
                switch (typeof x[key]) {
                  case "boolean":
                    inheritedValue = false;
                    break;
                  case "string":
                    inheritedValue = "";
                    break;
                  case "number":
                    inheritedValue = 0;
                    break;
                  default:
                    break;
                }
              }
            }

            if (!(inheritedValue !== undefined)) {
              console.assert(false);
            }
            return { ...x, [key]: inheritedValue };
          }
          return x;
        });

        replaceOldOptionSet(state, oldOptionSet, { ...oldOptionSet, options });
      }
    },

    discardOptionSetPropOverride(
      state,
      action: PayloadAction<{ oldOptionSet: PlanLibraryOptionSet; key: keyof PlanLibraryOptionSet }>,
    ) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      const { oldOptionSet, key } = action.payload;

      if (planLibraryDiscardOptionSetPropOverride(state.overrides, oldOptionSet.id, key)) {
        const inherited = state.inheritedOptionSets[oldOptionSet.id];
        let inheritedValue = inherited[key];
        if (inheritedValue === undefined) {
          if (key === "salesCategory") {
            inheritedValue = SalesCategory.UNCATEGORIZED;
          } else {
            switch (typeof oldOptionSet[key]) {
              case "boolean":
                inheritedValue = false;
                break;
              case "string":
                inheritedValue = "";
                break;
              default:
                break;
            }
          }
        }
        if (!(inheritedValue !== undefined)) {
          console.assert(false);
        }

        replaceOldOptionSet(state, oldOptionSet, { ...oldOptionSet, [key]: inheritedValue });
      }
    },

    discardAllOverridesForPlan(state) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return state;
      }
      const overrides = {};
      // Don't discard showroomIntroOverrides
      for (const key in state.overrides) {
        if (state.overrides[key].hasOwnProperty("showroomIntroOverrides")) {
          overrides[key] = { showroomIntroOverrides: state.overrides[key].showroomIntroOverrides };
        }
      }
      switch (state.plans.planType) {
        case "master_plan":
          void updateMasterPlanOverrides(state.builder.id, state.plans.masterPlan.id, overrides, {});
          break;
        case "community_plan":
          void updateLocationPlanOverrides(
            state.builder.id,
            state.plans.location.id,
            state.plans.locationPlan.id,
            overrides,
            {},
          );
          break;
        case "lot":
          void updateLotCommunityPlan({
            builderId: state.builder.id,
            locationId: state.plans.location.id,
            lotId: state.plans.lot.id,
            lotCommunityPlanId: state.plans.lotCommunityPlan.id,
            lotCommunityPlanName: `${state.plans.locationPlan.name} on Lot ${state.plans.lot.lotNumber}`,
            overrides,
          });
          break;
      }

      const salesCategoryHierarchy = state.builder.salesConfig?.salesCategoryCustomization?.hierarchy;
      const everySalesCategoryName = salesCategoryNamesInHierarchy(salesCategoryHierarchy, true);

      // Re-apply parent overrides so items retain parent-assigned categories, sort order, etc.
      // after the current level's overrides are cleared. Without this, items revert to their
      // raw doc state because unchangedOptionSets has no overrides applied.
      const applyParentOverrides = (
        unchangedOptionSets: Doc["optionSets"],
        plans: PlanLibraryPlans,
      ): Doc["optionSets"] => {
        if (plans.planType === "master_plan") {
          return unchangedOptionSets;
        }
        if (plans.planType === "community_plan") {
          return mergeOptionTemplatesIntoOptionSets(unchangedOptionSets, [plans.masterPlan.overrides ?? {}]);
        }
        // Lot: unchangedOptionSets already has overrides baked in by the caller,
        // so no additional merge is needed.
        return unchangedOptionSets;
      };
      const effectiveOptionSets = applyParentOverrides(state.unchangedOptionSets, state.plans);

      const { booleanOptionSetsPerSalesCategory, nonBoolOptionSetsPerSalesCategory, elevationOptionSet } =
        planLibraryCreateSalesCategories(
          state.unchangedOptionSets,
          effectiveOptionSets,
          everySalesCategoryName,
          state.builder,
        );

      // When overrides are discarded, we need to push parent-moved items back to the end of their categories for display
      const parentSalesCategoryMap = getParentSalesCategoryMap(state.plans);
      if (Object.keys(parentSalesCategoryMap).length > 0) {
        pushParentMovedItemsToEnd(booleanOptionSetsPerSalesCategory, overrides, parentSalesCategoryMap);
        pushParentMovedItemsToEnd(nonBoolOptionSetsPerSalesCategory, overrides, parentSalesCategoryMap);
      }

      const newState: PlanLibraryState = {
        ...state,
        overrides,
        optionEffectOverrides: {},
        needsSaving: false,
        discardAllOverrides: false,
        booleanOptionSetsPerSalesCategory,
        nonBoolOptionSetsPerSalesCategory,
        elevationOptionSet,
      };

      return newState;
    },

    setHideEmptySalesCategories(state, action: PayloadAction<boolean>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      state.hideEmptySalesCategories = action.payload;
    },

    enableMassEditModal(state, action: PayloadAction<boolean>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      state.massEditing = action.payload;
    },

    setNeedsSaving(state, action: PayloadAction<boolean>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      state.needsSaving = action.payload;
    },

    setSearch(state, action: PayloadAction<string>) {
      if (state.state === PlanLibraryStateType.UNINITIALIZED) {
        return;
      }
      state.searchFor = action.payload;

      function isOptionSetFilteredBySearch(optionSet: PlanLibraryOptionSet) {
        if (state.state === PlanLibraryStateType.UNINITIALIZED) {
          return false;
        }
        return !stringApplySearch(
          Object.values(optionSet.options ?? {})
            .map(({ name, price, builderOptionCode, description }) => [
              name,
              price,
              builderOptionCode,
              description,
              optionSet.name,
              optionSet.builderOptionSetCode,
              optionSet.category,
              optionSet.salesCategory,
              optionSet.description,
            ])
            .join(""),
          state.searchFor,
        );
      }

      for (const category in state.salesCategorySizes) {
        state.salesCategorySizes[category] = 0;
      }

      Object.entries(state.booleanOptionSetsPerSalesCategory).forEach(([category, optionSets]) => {
        state.booleanOptionSetsPerSalesCategory[category] = optionSets.map((optionSet) => {
          const isFilteredBySearch = isOptionSetFilteredBySearch(optionSet);
          if (!isFilteredBySearch) {
            state.salesCategorySizes[category]++;
          }
          return isFilteredBySearch === optionSet.isFilteredBySearch ? optionSet : { ...optionSet, isFilteredBySearch };
        });
      });

      Object.entries(state.nonBoolOptionSetsPerSalesCategory).forEach(([category, optionSets]) => {
        state.nonBoolOptionSetsPerSalesCategory[category] = optionSets.map((optionSet) => {
          const isFilteredBySearch = isOptionSetFilteredBySearch(optionSet);
          if (!isFilteredBySearch) {
            state.salesCategorySizes[category]++;
          }
          return isFilteredBySearch === optionSet.isFilteredBySearch ? optionSet : { ...optionSet, isFilteredBySearch };
        });
      });

      state.primarySalesCategorySizes = getPrimarySalesCategoryCounts(
        state.salesCategoryNames,
        state.salesCategorySizes,
        state.builder.salesConfig?.salesCategoryCustomization?.hierarchy,
      );
    },
  },
});

export const planLibraryActions = planLibrarySlice.actions;
export const planLibraryReducer = planLibrarySlice.reducer;
